/**
 * colormaps.ts
 *
 * Single source of truth for every colormap used in the app.
 * Mixes the standard d3-scale-chromatic maps with our own custom ones,
 * all exposed under one `colormaps` object with the same call shape:
 *
 *   colormaps[name](t)  // t in [0, 1] -> "#rrggbb"
 *
 * Usage:
 *   import { colormaps } from './colormaps';
 *   const hex = colormaps['plasma'](0.5);
 *   const hex2 = colormaps['signal'](0.5);
 */

import {
  interpolateInferno,
  interpolatePlasma,
  interpolateViridis,
} from 'd3-scale-chromatic';

// ---------------------------------------------------------------------------
// Shared type: every colormap, d3-provided or custom, has this exact shape.
// ---------------------------------------------------------------------------

export type ColormapFn = (t: number) => string;

// ---------------------------------------------------------------------------
// Helpers for building custom maps from a handful of RGB control points
// instead of hand-written 100-300 entry lookup tables.
// ---------------------------------------------------------------------------

type RGB = [number, number, number];
type Oklab = [number, number, number]; // [L, a, b]

export interface InterpolatorOptions {
  /**
   * Color space for interpolation:
   * - 'oklab': Perceptually uniform, preserves chroma, avoids muddy desaturation dips.
   * - 'rgb': Standard sRGB linear interpolation.
   * @default 'rgb'
   */
  space?: 'oklab' | 'rgb';

  /**
   * If provided, quantizes the scale into N discrete steps.
   * Leverages retinal Mach bands so small differences appear as clear steps.
   */
  steps?: number;
}

const toHex = (n: number): string =>
  Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, '0');

const rgbToHex = ([r, g, b]: RGB): string =>
  `#${toHex(r)}${toHex(g)}${toHex(b)}`;

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

// --- Oklab conversions (Björn Ottosson, 2020) ---

function srgbToLinear(c: number): number {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

function linearRgbToOklab(r: number, g: number, b: number): Oklab {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  return [
    0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
    1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
    0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
  ];
}

function oklabToLinearRgb(L: number, a: number, b: number): [number, number, number] {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;

  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  return [
    +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s,
  ];
}

function linearToSrgb(c: number): number {
  const clamped = Math.max(0, Math.min(1, c));
  const v = clamped <= 0.0031308 ? 12.92 * clamped : 1.055 * clamped ** (1 / 2.4) - 0.055;
  return Math.round(v * 255);
}

/**
 * Quantize any continuous ColormapFn into `steps` discrete levels.
 * Leverages retinal Mach bands so small differences between neighboring values
 * pop into distinct, unmistakable color bands.
 */
export function quantizeColormap(fn: ColormapFn, steps = 24): ColormapFn {
  return (t: number) => {
    const clamped = Math.max(0, Math.min(1, t));
    if (clamped >= 1) return fn(1);
    const bin = Math.floor(clamped * steps);
    const quantizedT = (bin + 0.5) / steps;
    return fn(quantizedT);
  };
}

/**
 * Build a d3-style interpolator from an ordered list of RGB stops.
 *
 * @param colors    control point colors (RGB 0-255), in order along the scale
 * @param positions where each color sits on [0, 1]. Defaults to evenly spaced.
 * @param options   options for color space ('oklab' | 'rgb') and optional step quantization
 */
export function createInterpolator(
  colors: RGB[],
  positions?: number[],
  options?: InterpolatorOptions,
): ColormapFn {
  const n = colors.length;
  if (n < 2) throw new Error('createInterpolator needs at least 2 colors');

  const pos = positions ?? colors.map((_, i) => i / (n - 1));
  const space = options?.space ?? 'rgb';
  const steps = options?.steps;

  // Pre-convert control points to Oklab coordinates if requested
  const oklabColors: Oklab[] = space === 'oklab'
    ? colors.map(([r, g, b]) =>
        linearRgbToOklab(srgbToLinear(r), srgbToLinear(g), srgbToLinear(b)),
      )
    : [];

  return function interpolate(t: number): string {
    let clamped = Math.max(0, Math.min(1, t));

    if (steps && steps > 1) {
      if (clamped >= 1) {
        clamped = 1;
      }
      else {
        const bin = Math.floor(clamped * steps);
        clamped = (bin + 0.5) / steps;
      }
    }

    // find the segment [pos[i], pos[i+1]] containing `clamped`
    let i = 0;
    while (i < n - 2 && clamped > pos[i + 1]) i++;

    const segStart = pos[i];
    const segEnd = pos[i + 1];
    const localT
      = segEnd === segStart ? 0 : (clamped - segStart) / (segEnd - segStart);

    if (space === 'oklab') {
      const c0 = oklabColors[i];
      const c1 = oklabColors[i + 1];
      const L = lerp(c0[0], c1[0], localT);
      const a = lerp(c0[1], c1[1], localT);
      const b = lerp(c0[2], c1[2], localT);

      const [lr, lg, lb] = oklabToLinearRgb(L, a, b);
      const rgb: RGB = [linearToSrgb(lr), linearToSrgb(lg), linearToSrgb(lb)];
      return rgbToHex(rgb);
    }
    else {
      const c0 = colors[i];
      const c1 = colors[i + 1];
      const rgb: RGB = [
        lerp(c0[0], c1[0], localT),
        lerp(c0[1], c1[1], localT),
        lerp(c0[2], c1[2], localT),
      ];
      return rgbToHex(rgb);
    }
  };
}

// ---------------------------------------------------------------------------
// Custom colormaps
// ---------------------------------------------------------------------------

/**
 * "signal" — reconstructed from the original signalIntensity lookup table.
 * It's a straight 3-stop blend: blue -> gold -> red.
 */
// const interpolateSignal: ColormapFn = createInterpolator([
//   [0, 114, 189], // blue
//   [237, 177, 32], // gold
//   [255, 0, 0], // red
// ]);

/**
 * "signalClamped" — same 3 colors, but pushed toward the middle of the
 * range so the ends stay flat for longer, matching the saturation
 * behavior visible in the original lookup table (~27% flat on each end).
 */
const interpolateSignalClamped: ColormapFn = createInterpolator(
  [
    [0, 114, 189],
    [0, 114, 189],
    [237, 177, 32],
    [255, 0, 0],
    [255, 0, 0],
  ],
  [0, 0.27, 0.5, 0.73, 1],
);

/**
 * "parula" — MATLAB's default colormap, dark blue -> teal/green -> yellow.
 * Reconstructed from MATLAB's published 64-entry parula RGB table.
 */
const interpolateParula: ColormapFn = createInterpolator([
  [53, 42, 135],
  [54, 48, 147],
  [54, 55, 160],
  [53, 61, 173],
  [50, 67, 186],
  [44, 74, 199],
  [32, 83, 212],
  [15, 92, 221],
  [3, 99, 225],
  [2, 104, 225],
  [4, 109, 224],
  [8, 113, 222],
  [13, 117, 220],
  [16, 121, 218],
  [18, 125, 216],
  [20, 129, 214],
  [20, 133, 212],
  [19, 137, 211],
  [16, 142, 210],
  [12, 147, 210],
  [9, 152, 209],
  [7, 156, 208],
  [6, 160, 205],
  [6, 164, 202],
  [6, 167, 198],
  [7, 169, 194],
  [10, 172, 190],
  [15, 174, 185],
  [21, 177, 180],
  [29, 179, 175],
  [37, 181, 169],
  [46, 183, 164],
  [56, 185, 158],
  [66, 187, 152],
  [77, 188, 146],
  [89, 189, 140],
  [101, 190, 134],
  [113, 191, 128],
  [124, 191, 123],
  [135, 191, 119],
  [146, 191, 115],
  [156, 191, 111],
  [165, 190, 107],
  [174, 190, 103],
  [183, 189, 100],
  [192, 188, 96],
  [200, 188, 93],
  [209, 187, 89],
  [217, 186, 86],
  [225, 186, 82],
  [233, 185, 78],
  [241, 185, 74],
  [248, 186, 68],
  [253, 190, 61],
  [255, 195, 55],
  [254, 200, 50],
  [252, 206, 46],
  [250, 211, 42],
  [247, 216, 38],
  [245, 222, 33],
  [244, 228, 29],
  [245, 235, 24],
  [246, 243, 19],
  [249, 251, 14],
]);

/**
 * "interpolateSignalHighContrast" (jetExpanded)
 *
 * High-contrast, jewel-toned palette engineered for discerning subtle local variations:
 * - Traverses an elegant, wide-gamut chromatic arc:
 *   Midnight Navy -> Royal Lapis -> Ocean Cerulean -> Seafoam Jade ->
 *   Champagne Gold -> Apricot Coral -> Berry Rose -> Luminous Pearl
 * - Interpolated in Oklab color space to maintain silky transitions, uniform perceptual
 *   delta-E, and rich chroma without muddy middle tones or garish neon clashes.
 */
const interpolateSignalHighContrast: ColormapFn = createInterpolator(
  [
    [0, 22, 54], // 0.00: Deep Midnight Navy (velvety dark anchor)
    [28, 68, 172], // 0.14: Royal Lapis (rich cobalt blue)
    [12, 138, 186], // 0.28: Ocean Cerulean (crisp blue-cyan)
    [36, 182, 142], // 0.42: Seafoam Jade (fresh mint green)
    [238, 252, 78], // 0.56: Champagne Gold (luminous warm pivot)
    [242, 118, 64], // 0.70: Apricot Coral (vivid warm sunset)
    [255, 30, 30], // 0.84: Berry Rose (refined ruby magenta)
    [255, 10, 10], // 1.00: Luminous Pearl (radiant highlight)
    [255, 0, 0], // 1.00: Luminous Pearl (radiant highlight)
  ],
  [0, 0.14, 0.28, 0.42, 0.56, 0.70, 0.84, 0.95, 1.0],
  { space: 'oklab' },
);

/**
 * Stepped 24-level version of Signal High-Contrast.
 * Sharp contour boundaries (Mach bands) make even 1-2% shifts instantly visible.
 */
const interpolateSignalHighContrastStepped: ColormapFn = quantizeColormap(
  interpolateSignalHighContrast,
  24,
);

// ---------------------------------------------------------------------------
// Combined export — drop-in for `colormaps[name](value)`
// ---------------------------------------------------------------------------

export const colormaps: Record<string, ColormapFn> = {
  // d3-scale-chromatic
  plasma: interpolatePlasma,
  viridis: interpolateViridis,
  inferno: interpolateInferno,
  // jet: interpolateTurbo,
  // jetExpanded: interpolateSignalHighContrast,
  jetExpandedStepped: interpolateSignalHighContrastStepped,
  // custom
  // signal: interpolateSignal,
  bmoreSignal: interpolateSignalClamped,
  parula: interpolateParula,
};
